import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';

export const EXCEPTION_TYPE_LABELS = {
  missing_clock_out: 'Missing clock-out',
  shift_too_long: 'Shift too long',
  missing_break: 'Missing break',
  overtime: 'Overtime',
  overlapping_entries: 'Overlapping entries',
  duplicate_entry: 'Duplicate entry',
  manual_time_change: 'Manual time change',
  job_hours_mismatch: 'Allocation mismatch',
  missing_job_allocation: 'Missing allocation',
  payroll_exception: 'Payroll flag',
};

export const utcDate = (v) => new Date(typeof v === 'string' && !v.endsWith('Z') && !v.includes('+') ? `${v}Z` : v);
export const fmtTime = (v) => utcDate(v).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
export const fmtDate = (v) => utcDate(v).toLocaleDateString([], { month: 'short', day: 'numeric' });
export const fmtDateTime = (v) => utcDate(v).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
export const toLocalInput = (v) => {
  if (!v) return '';
  const d = utcDate(v);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SEVERITY = {
  high:   { bg: 'rgba(239,68,68,0.15)',  color: '#F87171' },
  medium: { bg: 'rgba(255,160,0,0.15)',  color: '#FBBF24' },
  low:    { bg: 'rgba(160,174,192,0.15)', color: '#A0AEC0' },
};

export const SeverityBadge = ({ severity }) => {
  const s = SEVERITY[severity] || SEVERITY.low;
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide"
      style={{ background: s.bg, color: s.color }}>
      {severity}
    </span>
  );
};

export const StatusPill = ({ status }) => {
  const map = {
    submitted:   { label: 'Submitted',   bg: 'rgba(30,136,229,0.15)', color: '#60A5FA' },
    resubmitted: { label: 'Resubmitted', bg: 'rgba(30,136,229,0.15)', color: '#60A5FA' },
  };
  const s = map[status] || { label: status || '—', bg: 'rgba(160,174,192,0.15)', color: '#A0AEC0' };
  return (
    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
};

export const NotesModal = ({ title, description, minLen = 10, placeholder, submitLabel, destructive, busy, onSubmit, onClose, testId }) => {
  const [text, setText] = useState('');
  const ok = text.trim().length >= minLen;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Textarea value={text} rows={3} placeholder={placeholder}
          onChange={(e) => setText(e.target.value)} data-testid={`${testId}-input`} />
        <p className={`text-[11px] text-right ${ok ? 'text-slate-500' : 'text-amber-400'}`}>
          {text.trim().length}/{minLen} characters minimum
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid={`${testId}-cancel`}>Cancel</Button>
          <Button onClick={() => onSubmit(text.trim())} disabled={!ok || busy}
            className={destructive ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-[#00C896] hover:bg-emerald-600 text-white'}
            data-testid={`${testId}-submit`}>
            {busy ? 'Working…' : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
